import { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Navigate,
  useMatch,
  useNavigate,
  useParams,
  Link as RouterLink,
} from "react-router-dom";
import { Form, FormikProvider, useFormik } from "formik";
import Card from "@mui/material/Card";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { LoadingButton } from "@mui/lab";
import Page from "../Page";
import { BackButton } from "src/common/components/BackButton";
import { Loader } from "../Loader";
import { useAuth } from "src/hooks/useAuth.hook";
import { useAccess } from "src/hooks/useAccess.hook";
import { useGetDetail, useMutate } from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { alertSlice } from "src/store/alert/alert.slice";
import { FEATURE } from "src/util/enums";
import { is } from "src/util/is";
import sanitizeValues from "src/utils/sanitizeValues";
import {
  getTeamUrl,
  fleetOperatorUrl,
  fleetOperatorIdUrl,
} from "src/react-query/endPoints";
import {
  FLEET_OPERATOR_STATUS_OPTIONS,
  fleetOperatorValidationSchema,
  getFleetOperatorDefaultValues,
  emptyToNull,
} from "./fleet.constants";

export const FleetOperatorForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { teamId } = useSelectedTeam();
  const { id } = useParams();
  const isCreate = Boolean(useMatch("/fleet/operators/new"));
  const createAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "C");
  const updateAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "U");
  const notAuthorized = useAccess(FEATURE.FLEET_MANAGEMENT, "N");

  const { data: operator, isFetching } = useGetDetail("fleetOperator", { id });
  const { mutate, isLoading } = useMutate("fleetOperators");

  const isFleetEnabled =
    user?.company?.enabledFeatures?.includes(FEATURE.FLEET_MANAGEMENT) ?? false;

  useEffect(() => {
    if ((isCreate && !createAccess) || (!isCreate && notAuthorized)) {
      dispatch(
        alertSlice.actions.setAlert({ open: true, message: "Not Authorized" })
      );
      navigate("/fleet");
    }
  }, [dispatch, isCreate, createAccess, notAuthorized, navigate]);

  const onSubmit = (values, { setSubmitting }) => {
    const body = emptyToNull(sanitizeValues(values));
    const mutateObject = isCreate
      ? { url: getTeamUrl(teamId, fleetOperatorUrl), method: "POST" }
      : { url: getTeamUrl(teamId, fleetOperatorIdUrl(id)), method: "PUT" };
    mutate(
      { ...mutateObject, body },
      { onSuccess: () => navigate(isCreate ? "/fleet" : `/fleet/operators/${id}`) }
    );
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: getFleetOperatorDefaultValues(),
    validationSchema: fleetOperatorValidationSchema,
    onSubmit,
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, resetForm } =
    formik;

  useEffect(() => {
    if (!isCreate && !is.empty(operator)) {
      const defaults = getFleetOperatorDefaultValues();
      const values = Object.keys(defaults).reduce((acc, key) => {
        acc[key] = operator?.[key] ?? defaults[key] ?? "";
        return acc;
      }, {});
      resetForm({ values });
    }
  }, [isCreate, operator, resetForm]);

  if (!isFleetEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!isCreate && isFetching) {
    return <Loader />;
  }

  const textField = (name, label, extra = {}) => (
    <FormControl fullWidth size="small">
      <TextField
        size="small"
        label={label}
        disabled={!isCreate && !updateAccess}
        {...getFieldProps(name)}
        error={Boolean(touched[name] && errors[name])}
        helperText={touched[name] && errors[name]}
        {...extra}
      />
    </FormControl>
  );

  return (
    <Page title="Fleet Operator | Smart-CMS">
      <BackButton
        text={isCreate ? "Create Fleet Operator" : "Edit Fleet Operator"}
        path="/fleet"
      />
      <Card sx={{ p: 2 }}>
        <FormikProvider value={formik}>
          <Form
            autoComplete="off"
            id="fleet-operator-form"
            noValidate
            onSubmit={handleSubmit}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {textField("name", "Name", { required: true })}
              </Grid>
              <Grid item xs={12} md={6}>
                {textField("legal_name", "Legal Name")}
              </Grid>
              <Grid item xs={12} md={6}>
                {textField("contact_name", "Contact Name")}
              </Grid>
              <Grid item xs={12} md={6}>
                {textField("contact_email", "Contact Email", { type: "email" })}
              </Grid>
              <Grid item xs={12} md={6}>
                {textField("contact_phone", "Contact Phone")}
              </Grid>
              <Grid item xs={12} md={6}>
                {textField("billing_email", "Billing Email", { type: "email" })}
              </Grid>
              <Grid item xs={12} md={6}>
                {textField("fleet_type", "Fleet Type", {
                  placeholder: "e.g. Logistics, Taxi, Corporate",
                })}
              </Grid>
              <Grid item xs={12} md={6}>
                {textField("external_ref", "External Reference")}
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="fleet-operator-status">Status</InputLabel>
                  <Select
                    labelId="fleet-operator-status"
                    label="Status"
                    disabled={!isCreate && !updateAccess}
                    {...getFieldProps("status")}
                  >
                    {FLEET_OPERATOR_STATUS_OPTIONS.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Stack direction="row" spacing={2} mt={3}>
              <LoadingButton
                size="large"
                type="submit"
                variant="contained"
                disabled={!isCreate && !updateAccess}
                loading={isSubmitting || isLoading}
              >
                Save
              </LoadingButton>
              <LoadingButton
                size="large"
                type="button"
                variant="outlined"
                component={RouterLink}
                to={isCreate ? "/fleet" : `/fleet/operators/${id}`}
              >
                Cancel
              </LoadingButton>
            </Stack>
          </Form>
        </FormikProvider>
      </Card>
    </Page>
  );
};

export default FleetOperatorForm;
